// Page-wide UI state shared between the filter bar, table headers, pagination,
// and (later) the slide-panel. Server data lives in React Query — this store
// only tracks transient screen state per PRD 07 §5.
//
// Slice 2 surfaced search + sort. Slice 3 adds the current page. Editing
// (Slice 5) and dirty-list interaction (Slice 9) extend this store further.

import { create } from 'zustand';

// PRD §4.1 sortable columns. `pids` is non-sortable (array column).
export type GuidelineSortKey = 'gid' | 'name' | 'grade' | 'linkedItem' | 'deployedAt';
export type SortDir = 'asc' | 'desc';

// PRD §3.2 — slide panel mode. `null` is closed; `create` opens an empty
// form; `{ mode: 'edit', gid }` opens with the detail of that guideline.
export type PanelTarget =
  | null
  | { mode: 'create' }
  | { mode: 'edit'; gid: string };

interface ManagementGuidelinesPageState {
  search: string;
  sortKey: GuidelineSortKey;
  sortDir: SortDir;
  page: number;
  panel: PanelTarget;

  // Search change resets to page 1 so the user always sees the top of the
  // filtered list (matches prototype behavior). Sort change keeps the current
  // page intact.
  setSearch: (q: string) => void;
  toggleSort: (key: GuidelineSortKey) => void;
  setPage: (p: number) => void;
  openCreate: () => void;
  openEdit: (gid: string) => void;
  closePanel: () => void;
  reset: () => void;
}

// PRD §3.1 default sort, first page, panel closed.
const INITIAL = {
  search: '',
  sortKey: 'gid' as const,
  sortDir: 'asc' as const,
  page: 1,
  panel: null,
};

export const useManagementGuidelinesPageStore = create<ManagementGuidelinesPageState>(
  (set) => ({
    ...INITIAL,
    setSearch: (q) => set({ search: q, page: 1 }),
    toggleSort: (key) =>
      set((s) =>
        s.sortKey === key
          ? { sortDir: s.sortDir === 'asc' ? 'desc' : 'asc' }
          : { sortKey: key, sortDir: 'asc' },
      ),
    setPage: (p) => set({ page: Math.max(1, p) }),
    openCreate: () => set({ panel: { mode: 'create' } }),
    openEdit: (gid) => set({ panel: { mode: 'edit', gid } }),
    closePanel: () => set({ panel: null }),
    reset: () => set(INITIAL),
  }),
);
